/* eslint-disable jsx-a11y/control-has-associated-label */
import { forwardRef, ReactNode, useCallback, useImperativeHandle, useMemo } from 'react';
import { Control, useFieldArray, useForm, UseFormRegister, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  PostTossMaterialAcquisitionStatusesBody,
  TossMaterialAcquisition,
  TossMaterialAcquisitionStatus,
} from '../../../../../api/model';
import { CheckBox } from '../../../../atoms/CheckBox';
import SingleSelect from '../../../../atoms/SingleSelect';
import { CancelButton } from '../FormElements';

interface AcqusitionStatusRowProps {
  i: number; // 行番号
  data: TossMaterialAcquisition; // 行の取得率データ
  pre: TossMaterialAcquisition | undefined; // 一つ前の取得率データ
  acqusitionStatuses: TossMaterialAcquisitionStatus[]; // 取得率ステータスのマスターデータ
  editable: boolean; // 編集モードかどうか
  control: Control<{
    toss_material_acquisition_statuses: TossMaterialAcquisition[];
  }>; // useForm で返ってくる control
  register: UseFormRegister<{
    toss_material_acquisition_statuses: TossMaterialAcquisition[];
  }>; // useForm で返ってくる register
}

/**
 * トス資料取得率のテーブルの各行のコンポーネント
 * @param param0
 * @returns
 */
function AcquisitionStatusRow({
  i,
  data,
  pre,
  acqusitionStatuses,
  editable,
  control,
  register,
}: AcqusitionStatusRowProps) {
  // 言語情報
  const { t } = useTranslation();

  // この行の「取得不要」の値
  const noAcquisitionNecessary = useWatch({
    control,
    name: `toss_material_acquisition_statuses.${i}.no_acquisition_necessary`,
  });

  // 「状況」の表示内容
  let acqusitionStatusElem: ReactNode =
    noAcquisitionNecessary || data.acquisition_status === '不要' ? t('不要') : data.acquisition_status;

  // 「状況」はサービススペックの場合のみ特殊な表示になる。「取得不要」が OFF の場合はユーザーが選択
  if (data.toss_material_category_id === 2 && acqusitionStatusElem !== '不要') {
    acqusitionStatusElem = (
      <SingleSelect
        name={`toss_material_acquisition_statuses.${i}.toss_material_acquisition_status_id`}
        allowEmpty={false}
        options={acqusitionStatuses}
        register={register}
        getValue={(y) => y.toss_material_acquisition_status_id}
        getLabel={(y) => y.acquisition_status}
        isDisabled={!editable}
      />
    );
  }

  let groupName = '';
  if (data.toss_material_subgroup_id !== pre?.toss_material_subgroup_id && data.toss_material_subgroup_id != null) {
    groupName += data.group_name ?? '';
    // グループ名とサブグループ名は > でつなげる
    groupName += t(' > ').toString() + (data.subgroup_name ?? '');
  } else if (data.toss_material_group_id !== pre?.toss_material_group_id) {
    groupName += data.group_name ?? '';
  }

  return (
    <tr>
      {/* カテゴリ名（タブ名） */}
      <td>{data.toss_material_category_id !== pre?.toss_material_category_id ? data.category_name : undefined}</td>
      {/* グループ名 */}
      <td>{groupName}</td>
      {/* トス資料名 */}
      <td>{data.material_name}</td>
      {/* 取得不要チェックボックス */}
      <td>
        <span className="ck-box">
          <CheckBox
            label={undefined}
            name={`toss_material_acquisition_statuses.${i}.no_acquisition_necessary`}
            register={register}
            isDisabled={!editable}
            value={undefined}
          />
        </span>
      </td>
      {/* 担当 */}
      <td>{data.file_uploader}</td>
      {/* 状況 */}
      <td>{acqusitionStatusElem}</td>
    </tr>
  );
}

/**
 * トス資料取得率タブを親コンポーネントから操作するためのもの
 */
export interface AcquisitionStatusesHandler {
  validate: () => Promise<boolean>; // 入力内容をバリデーション
  submit: () => Promise<void>; // フォームの送信
  refresh: () => void; // 表示内容をリフレッシュ
}

interface Props {
  data: TossMaterialAcquisition[]; // トス利用取得率のリスト
  editable: boolean; // トス資料取得率画面が編集モードかどうか
  acqusitionStatuses: TossMaterialAcquisitionStatus[]; // トス資料取得率ステータスマスタデータ
  edit: () => Promise<void>; // 編集ボタン押下時の動作
  cancel: () => Promise<void>; // キャンセルボタン押下時の動作
  save: (data: PostTossMaterialAcquisitionStatusesBody) => Promise<void>; // 保存ボタン押下時の動作
}

/**
 * トス資料取得率タブのコンポーネント
 */
const AcquisitionStatusesTab = forwardRef<AcquisitionStatusesHandler, Props>(
  ({ data, editable, acqusitionStatuses, edit, cancel, save }, ref) => {
    // 言語情報
    const { t } = useTranslation();

    // フォーム用のパラメータ
    const { register, control, handleSubmit, reset } = useForm({
      defaultValues: { toss_material_acquisition_statuses: data },
    });

    // フォームが配列になるときに用いる。取得率のテーブル型フォームで利用
    const { fields } = useFieldArray({
      control,
      name: 'toss_material_acquisition_statuses',
    });

    // 取得率
    const rate = useMemo(() => {
      // 全体の取得率を n/m で表示する
      const n = data.filter((x) => x.toss_material_acquisition_status_id === 2 && !x.no_acquisition_necessary).length;
      const m = data.filter((x) => !x.no_acquisition_necessary).length;
      return `${n}/${m}`;
    }, [data]);

    // バリデーション
    const validate = useCallback(async () => {
      let result = false as boolean;
      await handleSubmit(() => {
        result = true;
      })();
      return result;
    }, [handleSubmit]);

    // 保存ボタンが押されたときの処理
    const submit = useCallback(async () => {
      await handleSubmit(save)();
    }, [handleSubmit, save]);

    // タブをリフレッシュするときの動作
    const refresh = useCallback(() => {
      reset({ toss_material_acquisition_statuses: data });
    }, [data, reset]);

    // 親コンポーネントから操作できるようにする
    useImperativeHandle(ref, () => ({
      validate,
      submit,
      refresh,
    }));

    return (
      <div id="tab2-content5" className="tab-content2">
        <div className="master-navi navisep">
          <ul>
            {/* 編集ボタン */}
            <li className="master-navibtn3">
              <button type="button" className="text-button" onClick={edit} disabled={editable}>
                <i className="fa-solid fa-pen-to-square" />
                {t('編集')}
              </button>
            </li>
            {/* キャンセルボタン */}
            <li className="master-navibtn4">
              <CancelButton editable={editable} cancel={cancel} />
            </li>
          </ul>
        </div>
        {/* 取得率の表示 */}
        <div className="acrate">
          <span>
            <span>
              <span className="txt">{t('取得率')}</span>
              <span className="num">{rate}</span>
            </span>
          </span>
        </div>
        {/* 取得率の表 */}
        <table className="table13">
          <tbody>
            <tr>
              <th>{t('トス資料')}</th>
              <th />
              <th />
              <th>{t('取得不要')}</th>
              <th>{t('担当')}</th>
              <th>{t('状況')}</th>
            </tr>
            {fields.map((x, i, arr) => {
              const pre = i === 0 ? undefined : arr[i - 1];
              return (
                <AcquisitionStatusRow
                  key={x.id}
                  i={i}
                  data={x}
                  pre={pre}
                  acqusitionStatuses={acqusitionStatuses}
                  editable={editable}
                  control={control}
                  register={register}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
);

export default AcquisitionStatusesTab;
